import { IStartPayload } from "./client";

export interface Config {
  publicKey: string;
  endPoint?: string;
  debug?: boolean;
  audioSource?: {
    deviceId?: string;
    constraints?: MediaTrackConstraints;
  };

  [key: string]: any;
}

export type VoiceProvider = 'elevenlabs' | 'rime' | 'openai' | 'cartesia' | 'playht' | 'neets' | 'deepgram' | 'dubverse';

interface Tool { // Main structure for defining a function.
  name: string;           // Function name, formatted as a valid identifier (no spaces, begins with a letter)
  description: string;    // Detailed description to help the agent understand when to use the function
  webhook: string;        // The URL of the webhook to which the request will be sent
  header: object;         // Any necessary headers for the webhook request. Ex: {"Content-Type": "application/json"}
  params: Param[];        // Array of `Param` objects, defining the parameters needed by the function
}

interface Param {
  name: string;           // Valid parameter name, no spaces, starts with a letter, can use underscores or camelCase
  type: "string" | "number" | "boolean";        // The data type of the parameter (e.g., string, number, boolean)
  description: string;    // Detailed description of the parameter
  required: boolean;      // Whether this parameter is mandatory
}

export interface Voice {
  provider: VoiceProvider;
  voice_id: string;
}

interface AgentSetting {
  first_message?: string;
  vad_threshold?: number;
  privacy_settings?: {
    opt_out_data_collection: boolean;
  };
  session_timeout?: {
    max_duration: number;
    max_idle: number;
    message?: string;
  },
  custom_vocabulary?: {
    keywords?: object;
  };
  flow?: {
    user_start_first?: boolean;
    interruption?: {
      allowed: boolean;
      keep_interruption_message: boolean;
    };
    response_delay?: number;
  }
}

interface WebhookSetting {
  session_data_webhook?: string;
  extra_prompt_webhook?: string;
}

type AllSettings = AgentSetting & WebhookSetting;

export interface AgentConfig extends AllSettings {
  prompt?: string;
  voice?: Voice;
  tools?: Tool[];
  language?: string;
  custom_llm_websocket?: string;
  llm?: {model: string};
  [key: string]: any;
}

export interface ClientEvents {
  'onopen': () => void;
  'onready': (payload?: { session_id?: string }) => void;
  'onsessionended': () => void;
  'onaudio': (audio: Uint8Array) => void;
  'onresponsetext': (text: string, payload: { is_final?: boolean }) => void;
  'ontranscript': (text: string, payload: { is_final?: boolean }) => void;
  'analyzer': (analyzer: AnalyserNode) => void;
  'useraudioready': (data: { analyser: AnalyserNode, stream: MediaStream }) => void;
  'onlatency': (latency: number) => void;
  'onclose': (event: CloseEvent) => void;
  'onerror': (error: Event) => void;
  'onagentstate': (state: AgentState) => void;
  'ai_action': (action: any) => void;
  [key: string]: any;
}

export interface ClientInterface {
  start(payload: IStartPayload | AgentConfig | string, metadata?: object, includeMetadataInPrompt?: boolean): Promise<void>;
  stop(): Promise<void>;
  mute(): void;
  unmute(): void;
}

export enum AgentState {
  IDLE = "idle",
  PREPARE_ANSWER = "prepare_answer",
  ANSWER = "answer",
  PAUSE = "pause",
}